import React, { useState, useMemo } from 'react'

import { LinkOutlined } from '@ant-design/icons'
import { MultipleValue, SelectValue } from '@shared/table/Rules/component'
import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule } from '@shared/table/TableIndex'
import { Tag } from 'antd'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface MerchantOption {
  label: string;
  value: string;
}

interface SalesChannelsListProps {
  role?: 'admin' | 'operator';
  service: TableService; // New prop
  merchantOptions?: MerchantOption[];
  hideMerchantUI?: boolean; // Operator single-merchant 模式使用
}

interface SalesChannelRow {
  id: string;
  name?: string;
  merchant_name?: string;
  extra_name?: string;
  enabled?: boolean;
  created_at?: string;
  [key: string]: unknown;
}

const SalesChannelsList: React.FC<SalesChannelsListProps> = ({
  role = 'admin',
  service, // New prop
  merchantOptions = [],
  hideMerchantUI = false // Operator single-merchant 模式使用
}) => {
  const { t } = useTranslation()
  const [lastModified, setLastModified] = useState(0)

  const urlPrefix = role === 'operator' ? '/operator' : '/admin'
  const basePath = role === 'operator' ? '/data/sales_channels' : '/sales_channels'

  const columns = useMemo(() => {
    const cols: unknown[] = []
    cols.push(
      {
        nameKey: `models.sales_channel.name`,
        persist: true,
        sortable: true,
        sortFieldName: 'sales_channels.name',
        minWidth: '120px',
        wrap: true,
        cell: ({ id, name }: SalesChannelRow) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`${basePath}/${id}`} style={{ wordBreak: 'break-word' }}>
              {name || '-'}
            </Link>
            <a
              href={`${urlPrefix}${basePath}/${id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        ),
      },
      ...(role === 'operator' && !hideMerchantUI ? [
        {
          nameKey: `models.merchant.name`,
          persist: true,
          sortable: true,
          sortFieldName: 'merchants.name',
          minWidth: '180px',
          wrap: true,
          selector: ({ merchant_name }: SalesChannelRow) => merchant_name || '-',
        }
      ] : []),
      {
        nameKey: `models.sales_channel.extra_name`,
        sortable: true,
        sortFieldName: 'sales_channels.extra_name',
        minWidth: '300px',
        wrap: true,
        selector: ({ extra_name }: SalesChannelRow) => extra_name || '-',
      },
      {
        nameKey: `models.sales_channel.enabled`,
        minWidth: '100px',
        cell: ({ enabled }: SalesChannelRow) => {
          if (enabled) {
            return <Tag color="success">{t('common.enabled')}</Tag>
          }
          return <Tag color="default">{t('common.disabled')}</Tag>

        }
      },
      {
        nameKey: `common.created_at`,
        sortable: true,
        sortFieldName: 'sales_channels.id',
        minWidth: '160px',
        wrap: true,
        cell: ({ created_at }: SalesChannelRow) => created_at ? dayjs(created_at).format('YYYY-MM-DD HH:mm:ss') : '-',
      }
    )

    return cols
  }, [role, urlPrefix, t, hideMerchantUI])

  const supportRules = useMemo(() => {
    const rules: SupportRule[] = []

    // Merchant 篩選器 (Operator 專用; single-merchant 模式下隱藏)
    if (role === 'operator' && !hideMerchantUI) {
      rules.push({
        label: t(`models.inventory.merchant_id`),
        component: MultipleValue,
        inputName: 'merchants.id',
        defaultPinned: true,
        createable: false,
        defaultOptions: merchantOptions,
      })
    }

    rules.push(
      {
        label: t(`models.sales_channel.name`),
        component: MultipleValue,
        inputName: 'sales_channels.name',
        defaultPinned: true,
        createable: true,
      },
      {
        label: t(`models.sales_channel.extra_name`),
        component: MultipleValue,
        inputName: 'sales_channels.extra_name',
        createable: true,
      },
      {
        label: t(`models.sales_channel.enabled`),
        component: SelectValue,
        inputName: 'sales_channels.enabled',
        defaultOptions: [
          {
            label: t('common.enabled'),
            value: 'true',
          },
          {
            label: t('common.disabled'),
            value: 'false',
          },
        ],
      }
    )

    return rules
  }, [role, t, merchantOptions, hideMerchantUI])

  return (
    <TableIndex
      columns={columns}
      searchable={{
        match: 'contains',
        searchFields: [
          t('models.sales_channel.name'),
          t('models.sales_channel.extra_name'),
        ],
      }}
      columnsEditable={true}
      tableId={`${role === 'operator' ? 'Operator' : 'Admin'}SalesChannelsTable`}
      defaultQuery={{
        sorting_field: 'sales_channels.id',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      lastModified={lastModified}
      setLastModified={setLastModified}
      service={service}
      filterHelpExample={t('shared.sales_channels.list.filter_help_example') as string}
      filterDocsUrl={`/docs/${role}/sales_channels/list#features`}
    />
  )
}

export default SalesChannelsList
